import Vue from 'vue';
import { formatPhoneNumber } from '../lib/utilities/formatPhoneNumber';
import { round } from 'lodash';

const DefaultCentsToDollarPrecision = 7;

const formatIntegerWithThousandsSeparator = (
  value: string,
  separator = ' '
): string => {
  /*
  This regex matches all the *positions* (not characters) in a number where a thousand separator would be placed. To illustrate, in the following examples the '|' character is used to signify where the regex would find position matches:
    - 1 -> 1
    - 10 -> 10
    - 100 -> 100
    - 1000 -> 1|000
    - 10000 -> 10|000
    - 100000 -> 100|000
    - 1000000 -> 1|000|000
  Said in more technical language: the regex matches non-word boundary positions which are followed by a set of 3 digits (or multiples thereof), followed by a non-digit (typically the end of the string).

  Using the regex and `String.replace`, the matched positions can then be 'replaced' with a thousand separator of choice.

  An example of the overall transformation is as follows (using a space as a separator): 1000000 -> 1|000|000 -> 1 000 000.
  */

  const number: number = +value;
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, separator);
};

export const formatDecimalToPercentage = (
  decimal: number,
  significantFigures = 0
): string => {
  if (decimal && decimal * 100 > 0 && decimal * 100 < 1) {
    return '< 1%';
  }
  return (decimal * 100).toFixed(significantFigures) + '%';
};

export const calculatePercantage = (value: number, total: number): string => {
  return total > 0 ? formatDecimalToPercentage(value / total) : '0%';
};

export const calculatePercentageValue = (
  decimal: number,
  significantFigures = 0
): number => {
  return +(decimal * 100).toFixed(significantFigures);
};

export function formatPercentageValue(value) {
  return calculatePercentageValue(value, 4) === 0
    ? '0%'
    : calculatePercentageValue(value, 4) < 1
    ? '<1%'
    : formatDecimalToPercentage(value);
}

export const toFixed = (value: number, digit: number) => {
  return value?.toFixed?.(digit) || value;
};

export const centsToDollars = (value: number | string): number => {
  const amount = +value / 100;
  return round(+amount, DefaultCentsToDollarPrecision);
};

export const dollarsToCents = (value: number | string): number => {
  const amount = +value * 100;
  return round(+amount, DefaultCentsToDollarPrecision);
};

export const formatCentsToDollars = (
  cents: number | string,
  maximumDecimalPlaces = DefaultCentsToDollarPrecision
): string => {
  const amount = `${round(centsToDollars(cents), maximumDecimalPlaces)}`;

  const [dollars, cent] = amount.split('.');

  const dollarString = formatIntegerWithThousandsSeparator(dollars);
  let centString = '';
  if (cent?.length === 1) centString = `.${cent}0`;
  else if (cent?.length) {
    centString = `.${cent}`;
  }
  return `${dollarString}${centString}`;
};

Vue.filter(
  'formatIntegerWithThousandsSeparator',
  formatIntegerWithThousandsSeparator
);
Vue.filter('formatDecimalToPercentage', formatDecimalToPercentage);
Vue.filter('toFixed', toFixed);
Vue.filter('formatPhoneNumber', formatPhoneNumber);
Vue.filter('formatCentsToDollars', formatCentsToDollars);
